import { apiClient } from '@/shared/data/client';
import { mapUploadTokenDto } from '@/shared/data/dtoMappers';
import type { UploadTokenDto } from '@flowqa/shared';

export type UploadTokenCreateInput = { name: string; expiresAt?: string | null };

export type UploadTokenCreateResponse = { token: string; tokenRecord: UploadTokenDto };

export type UploadTokenUpdateInput = { name?: string; expiresAt?: string | null };

export type UploadTokenTestInput = { token: string };

export type UploadTokenTestResult = {
  status: 'ok';
  projectId: string;
  endpoint: '/uploads';
  headerName: 'x-upload-token';
};

export const listUploadTokens = async (
  projectId: string,
  orgId?: string | null,
): Promise<UploadTokenDto[]> => {
  const result = await apiClient.get<UploadTokenDto[]>(`projects/${projectId}/upload-tokens`, {
    orgId,
  });
  return result.map(mapUploadTokenDto);
};

export const createUploadToken = async (
  projectId: string,
  input: UploadTokenCreateInput,
  orgId?: string | null,
): Promise<UploadTokenCreateResponse> => {
  const response = await apiClient.post<UploadTokenCreateResponse>(
    `projects/${projectId}/upload-tokens`,
    input,
    { orgId },
  );
  return { token: response.token, tokenRecord: mapUploadTokenDto(response.tokenRecord) };
};

export const revokeUploadToken = async (
  projectId: string,
  tokenId: string,
  orgId?: string | null,
): Promise<UploadTokenDto> => {
  const response = await apiClient.post<UploadTokenDto>(
    `projects/${projectId}/upload-tokens/${tokenId}/revoke`,
    undefined,
    { orgId },
  );
  return mapUploadTokenDto(response);
};

export const updateUploadToken = async (
  projectId: string,
  tokenId: string,
  input: UploadTokenUpdateInput,
  orgId?: string | null,
): Promise<UploadTokenDto> => {
  const response = await apiClient.patch<UploadTokenDto>(
    `projects/${projectId}/upload-tokens/${tokenId}`,
    input,
    { orgId },
  );
  return mapUploadTokenDto(response);
};

export const testUploadToken = async (
  projectId: string,
  input: UploadTokenTestInput,
  orgId?: string | null,
): Promise<UploadTokenTestResult> => {
  return await apiClient.post<UploadTokenTestResult>(
    `projects/${projectId}/upload-tokens/test`,
    input,
    { orgId },
  );
};
